import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import styles from './Menu.module.scss'

export interface MenuProps {
  /** The element that toggles the menu. Receives `open` for styling. */
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode
  children: ReactNode | ((close: () => void) => ReactNode)
  align?: 'left' | 'right'
  /** Open below the trigger (default) or above it, for triggers near the bottom of the screen. */
  placement?: 'bottom' | 'top'
  className?: string
}

/** Lightweight dropdown menu with outside-click and Escape handling. */
export function Menu({ trigger, children, align = 'right', placement = 'bottom', className }: MenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const close = () => setOpen(false)
  return (
    <div ref={ref} className={clsx(styles.wrap, className)}>
      {trigger({ open, toggle: () => setOpen((o) => !o) })}
      {open && (
        <div role="menu" className={clsx(styles.menu, align === 'left' && styles.left, placement === 'top' && styles.top)}>
          {typeof children === 'function' ? children(close) : children}
        </div>
      )}
    </div>
  )
}

export interface MenuItemProps {
  icon?: ReactNode
  onClick?: () => void
  to?: string
  href?: string
  danger?: boolean
  disabled?: boolean
  children: ReactNode
}

export function MenuItem({ icon, onClick, to, href, danger, disabled, children }: MenuItemProps) {
  const className = clsx(styles.item, danger && styles.danger)
  if (to) {
    return (
      <Link to={to} className={className} role="menuitem" onClick={onClick}>
        {icon}
        {children}
      </Link>
    )
  }
  if (href) {
    return (
      <a href={href} className={className} role="menuitem" target="_blank" rel="noopener noreferrer" onClick={onClick}>
        {icon}
        {children}
      </a>
    )
  }
  return (
    <button type="button" className={className} role="menuitem" onClick={onClick} disabled={disabled}>
      {icon}
      {children}
    </button>
  )
}

export function MenuDivider() {
  return <div className={styles.divider} role="separator" />
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className={styles.label}>{children}</div>
}

export function Tooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className={styles.tooltipWrap}>
      {children}
      <span className={styles.tooltip} role="tooltip">
        {label}
      </span>
    </span>
  )
}
